import { SearchOutlined } from '@ant-design/icons'
import {
  Row,
  Table,
  Tabs,
  type TablePaginationConfig,
  Input
} from 'antd'
import { type SorterResult } from 'antd/es/table/interface'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import SecureFormPostActionButton from '~/components/SecureFormPostActionButton'
import { RegionSelect } from '~/components/RegionSelect'
import {
  type AppstreamSession,
  useAppstreamSessionsQuery
} from '~/hooks/useAppstreamSessionsQuery'
import { regionCodeToName } from '~/utils/constants'
import { backendUrl, compareNumber, formatDateTime } from '~/utils/helpers'
import { useParticipantSessionsQuery } from './query'

type ReportTab = 'users' | 'sessions'

interface SessionSort {
  orderBy: string
  direction: 'asc' | 'desc'
}

const DEFAULT_SESSION_SORT: SessionSort = {
  orderBy: 'session_start_time',
  direction: 'desc'
}

const UtilizationReport: React.FC = () => {
  const { id } = useParams()
  const courseId = Number(id)
  const [activeTab, setActiveTab] = useState<ReportTab>('users')
  const [region, setRegion] = useState<string | null>(null)
  const [search, setSearch] = useState<string>('')
  const [sessionPagination, setSessionPagination] =
    useState<TablePaginationConfig>({
      current: 1,
      pageSize: 15
    })
  const [sessionSort, setSessionSort] =
    useState<SessionSort>(DEFAULT_SESSION_SORT)

  const { data, isLoading } = useParticipantSessionsQuery(courseId, {
    region_code: region
  })
  const {
    data: sessionsData,
    isFetching: isFetchingSessions
  } = useAppstreamSessionsQuery({
    course_id: courseId,
    region_code: region,
    search,
    page: sessionPagination.current ?? 1,
    per_page: sessionPagination.pageSize ?? 15,
    order_by: sessionSort.orderBy,
    direction: sessionSort.direction
  }, activeTab === 'sessions')

  const filteredData = useMemo(() => {
    if (data == null) {
      return []
    }

    const normalizedSearch = search.trim().toLowerCase()
    return data.data.filter((item) => {
      if (normalizedSearch === '') {
        return true
      }

      const fullName = [item.first_name, item.last_name].join(' ')
      return fullName.toLowerCase().includes(normalizedSearch) ||
        item.email.toLowerCase().includes(normalizedSearch) ||
        item.username.toLowerCase().includes(normalizedSearch)
    })
  }, [data, search])

  const handleSessionTableChange = (
    pagination: TablePaginationConfig,
    _filters,
    sorter: SorterResult<AppstreamSession> | Array<SorterResult<AppstreamSession>>
  ) => {
    const selectedSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const nextSort = selectedSorter.order == null
      ? DEFAULT_SESSION_SORT
      : {
          orderBy: String(selectedSorter.columnKey),
          direction: selectedSorter.order === 'ascend' ? 'asc' as const : 'desc' as const
        }

    setSessionPagination(pagination)
    setSessionSort(nextSort)
  }

  const resetSessionPage = () => {
    setSessionPagination((current) => ({ ...current, current: 1 }))
  }

  const userTable = <Table
    size='small'
    tableLayout='auto'
    dataSource={filteredData}
    loading={isLoading}
    rowKey='id'
    footer={() => (
      <Row justify='start'>
        <span>Total utilization: {data?.total_duration_in_hours} hours</span>
      </Row>
    )}
    expandable={{
      expandRowByClick: true,
      rowExpandable: item => item.appstream_sessions.length > 0,
      expandedRowRender: (record) => {
        return <Table
          className='radup-utilization-items'
          size='small'
          tableLayout='auto'
          dataSource={record.appstream_sessions}
          loading={isLoading}
          pagination={false}
          rowKey='id'
          columns={[
            {
              key: 'location',
              title: 'Location',
              render: value => regionCodeToName(value.region)
            },
            {
              key: 'Fleet Name',
              title: 'Fleet Name',
              dataIndex: 'fleet_name'
            },
            {
              key: 'session_start_time',
              title: 'Started at',
              render: value => formatDateTime(value.session_start_time)
            },
            {
              key: 'session_end_time',
              title: 'Ended at',
              render: value => formatDateTime(value.session_end_time)
            },
            {
              key: 'Duration',
              title: 'Duration',
              dataIndex: 'session_duration_in_seconds_time_span'
            }
          ]}
        />
      }
    }}
    columns={[
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id'
      },
      {
        key: 'name',
        title: 'Name',
        render: (value) => [value.first_name, value.last_name].join(' ')
      },
      {
        key: 'email',
        title: 'Email',
        dataIndex: 'email'
      },
      {
        key: 'username',
        title: 'Username',
        dataIndex: 'username'
      },
      {
        key: 'total_appstream_session_duration_in_seconds',
        title: 'Seconds',
        dataIndex: 'total_appstream_session_duration_in_seconds',
        sorter: (a, b) => compareNumber(a.total_appstream_session_duration_in_seconds, b.total_appstream_session_duration_in_seconds)
      },
      {
        key: 'total_appstream_session_duration_in_minutes',
        title: 'Minutes',
        dataIndex: 'total_appstream_session_duration_in_minutes',
        sorter: (a, b) => compareNumber(a.total_appstream_session_duration_in_minutes, b.total_appstream_session_duration_in_minutes)
      },
      {
        key: 'total_appstream_session_duration_in_hours',
        title: 'Hours',
        dataIndex: 'total_appstream_session_duration_in_hours',
        sorter: (a, b) => compareNumber(a.total_appstream_session_duration_in_hours, b.total_appstream_session_duration_in_hours)
      },
      {
        key: 'Duration',
        title: 'Duration',
        dataIndex: 'total_appstream_session_duration_time_span'
      }
    ]}
  />

  const sessionTable = <Table<AppstreamSession>
    size='small'
    tableLayout='auto'
    dataSource={sessionsData?.data}
    loading={isFetchingSessions}
    rowKey='id'
    pagination={{
      ...sessionPagination,
      total: sessionsData?.meta.total
    }}
    onChange={handleSessionTableChange}
    footer={() => (
      <Row justify='start'>
        <span>Total utilization: {sessionsData?.total_duration_in_hours} hours</span>
      </Row>
    )}
    columns={[
      {
        key: 'name',
        title: 'Name',
        render: (_, record) => record.participant == null
          ? ''
          : [record.participant.first_name, record.participant.last_name].join(' ')
      },
      {
        key: 'username',
        title: 'Username',
        dataIndex: 'user_id'
      },
      {
        key: 'email',
        title: 'Email',
        render: (_, record) => record.participant?.email ?? ''
      },
      {
        key: 'region',
        title: 'Location',
        render: (_, record) => regionCodeToName(record.region)
      },
      {
        key: 'fleet_name',
        title: 'Fleet Name',
        dataIndex: 'fleet_name'
      },
      {
        key: 'session_start_time',
        title: 'Started at',
        render: (_, record) => formatDateTime(record.session_start_time),
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_start_time'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      },
      {
        key: 'session_end_time',
        title: 'Ended at',
        render: (_, record) => formatDateTime(record.session_end_time),
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_end_time'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      },
      {
        key: 'session_duration_in_seconds',
        title: 'Duration',
        dataIndex: 'session_duration_in_seconds_time_span',
        sorter: true,
        sortOrder: sessionSort.orderBy === 'session_duration_in_seconds'
          ? sessionSort.direction === 'asc' ? 'ascend' : 'descend'
          : null
      }
    ]}
  />

  return <>
    <PageHeader
      className='radup-page-header'
      extra={[
        <RegionSelect
          key='regions'
          style={{ width: 200 }}
          allowClear
          showRegionCode={false}
          onSelect={item => {
            setRegion(item)
            resetSessionPage()
          }}
          onClear={() => {
            setRegion(null)
            resetSessionPage()
          }}
        />,
        <Input.Search
          key='search'
          placeholder='Search'
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={value => {
            setSearch(value.trim())
            resetSessionPage()
          }}
        />,
        <SecureFormPostActionButton
          key='download'
          actionUrl={backendUrl(`statistic/courses/${id}/export-ultilization-report`)}
          title='Download report'
        />
      ]}
    />

    <Tabs
      activeKey={activeTab}
      onChange={(key) => { setActiveTab(key as ReportTab) }}
      items={[
        {
          key: 'users',
          label: 'By user',
          children: userTable
        },
        {
          key: 'sessions',
          label: 'By session',
          children: sessionTable
        }
      ]}
    />
  </>
}

export default UtilizationReport
